import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, onKeyDown, ...props }, ref) => (
  // A11y: Radix (and the HTML spec) only toggle a checkbox on Space. Many
  // users — especially those navigating with one hand or expecting form-like
  // semantics — also press Enter to validate the focused control. We extend
  // the primitive so Enter behaves exactly like Space without breaking the
  // caller's own onKeyDown handler.
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      "peer h-4 w-4 shrink-0 rounded-xs border border-primary ring-offset-background data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    onKeyDown={(e) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (e.key === "Enter" && !e.repeat) {
        e.preventDefault();
        // Radix toggles on click; dispatching click() honours `disabled`,
        // form submission semantics and any onCheckedChange wiring.
        (e.currentTarget as HTMLButtonElement).click();
      }
    }}
    {...props}
  >
    <CheckboxPrimitive.Indicator className={cn("flex items-center justify-center text-current")}>
      <Check className="h-4 w-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
